import type { ClientConfig } from '@/client.config';

export type Theme = 'light' | 'dark' | 'system';
const STORAGE_KEY = 'crm.theme';

/** Resolves 'system' through the OS preference. */
export function resolveTheme(theme: Theme, prefersDark = matchMediaDark()): 'light' | 'dark' {
  return theme === 'system' ? (prefersDark ? 'dark' : 'light') : theme;
}

function matchMediaDark(): boolean {
  return (
    typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-color-scheme: dark)').matches
  );
}

export function readStoredTheme(): Theme | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'light' || v === 'dark' || v === 'system' ? v : null;
  } catch {
    return null;
  }
}

export function storeTheme(theme: Theme): void {
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    /* private mode or blocked storage: the choice just doesn't persist */
  }
}

/**
 * Applies the brand to the document: the accent becomes --primary, the resolved theme toggles
 * the `dark` class. Called once at boot and again when the user changes the theme.
 */
export function applyBrand(
  config: ClientConfig,
  theme: Theme = readStoredTheme() ?? config.defaultTheme,
): void {
  const root = document.documentElement;
  const resolved = resolveTheme(theme);
  // Swap every color at once: components animate `color`/`background`, and a theme change should
  // not show a half-transitioned frame (it also confuses contrast checks).
  root.classList.add('no-transitions');
  root.style.setProperty('--primary', config.accent);
  root.classList.toggle('dark', resolved === 'dark');
  root.style.colorScheme = resolved;
  document.title = `${config.name} · CRM`;
  void root.offsetHeight; // force style recalculation before re-enabling transitions
  requestAnimationFrame(() => root.classList.remove('no-transitions'));
}
